import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRetailerHistory } from '../hooks/useIntake';
import { marketName } from '../review.utils';
import type { RetailerChoiceEntry, RetailerChoiceInputEntry, RetailerHistory, RetailerHistoryEntry, RetailerSelection } from '../intake.api';

export interface RetailerChoiceBlockProps {
  /** De doelmarkten die in het aangeleverde bestand voorkomen, als driecijferige codes. */
  markets: string[];
  choice: RetailerChoiceEntry[];
  disabled?: boolean;
  /** Alles in één aanroep, elk land met zijn eigen lijst: één schrijfactie, nooit een halve batch. */
  onChoose: (entries: RetailerChoiceInputEntry[]) => void;
}

/**
 * h13-5 — het blok waarin de klant kiest naar welke afnemers zijn producten gaan.
 *
 * DE KEUZE IS PER DOELMARKT. Afnemers zijn per land een andere verzameling; een afnemer die de
 * markt van een product niet voert wordt verderop in de keten zonder melding weggelaten. Bij één
 * land is dat onzichtbaar (het land staat in de kop), bij meer landen krijgt elk land zijn eigen
 * regel. De schakelaar "dezelfde afnemers voor alle landen" hoort bij H13-8 en zit hier bewust niet.
 *
 * ELKE KLIK WORDT METEEN BEWAARD, niet bij het sluiten van het paneel en niet bij het indienen. Dat
 * is het hele punt van de eis dat een onderbroken aanmelding zijn keuze terugvindt: het gaat alleen
 * mis bij de klant die halverwege wegloopt, en die klaagt niet maar begint opnieuw.
 */
export function RetailerChoiceBlock({ markets, choice, disabled, onChoose }: RetailerChoiceBlockProps) {
  const { t } = useTranslation();
  const histories = useRetailerHistory(markets);

  /**
   * Het voorstel uit de historie: de afnemers die deze klant voor déze markt eerder koos. De
   * telling stuurt alleen de volgorde en wordt nooit getoond — "Albert Heijn (17×)" nodigt uit tot
   * rekenen over een gewoonte en liegt bij een klant die één keer is overgestapt.
   */
  const proposals = useMemo(
    () =>
      histories.map((query) =>
        (query.data?.retailers ?? [])
          .filter((entry) => entry.timesChosen > 0)
          // De volgorde ÍS het advies: het vaakst gekozen bovenaan. Zonder deze sortering staan de
          // chips in de volgorde van de afnemerstabel en zegt de bovenste naam niets.
          .slice()
          .sort((a, b) => b.timesChosen - a.timesChosen || a.name.localeCompare(b.name))
          .map(toSelection),
      ),
    [histories],
  );

  /**
   * Voorvinken gebeurt door het op te slaan, niet door het alleen te tonen: anders zou een klant
   * die niets aanraakt indienen met een keuze die nergens staat. Eén markt tegelijk, want elke
   * schrijfactie hoogt het versienummer op en de volgende zou dan op zijn eigen voorganger botsen.
   */
  const geprobeerd = useRef(new Set<string>());
  const autoFillIndex = markets.findIndex(
    (market, index) =>
      (proposals[index]?.length ?? 0) > 0 &&
      choice.find((entry) => entry.targetMarket === market) === undefined &&
      // Eén poging per markt per bezoek. Zonder dit slot draait een mislukkende schrijfactie rond:
      // het opslaan zet `disabled` aan en na de fout weer uit, en die overgang start het effect
      // opnieuw — met dezelfde markt, want de keuze staat er nog steeds niet.
      !geprobeerd.current.has(market),
  );
  useEffect(() => {
    if (autoFillIndex === -1 || disabled) return;
    const markt = markets[autoFillIndex]!;
    geprobeerd.current.add(markt);
    onChoose([{ targetMarket: markt, retailers: proposals[autoFillIndex] ?? [] }]);
    // `onChoose` en `proposals` zijn per render nieuw; de markt en zijn plaats zijn wat telt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFillIndex, markets[autoFillIndex], disabled]);

  /**
   * H13-8 — dezelfde afnemers voor alle landen.
   *
   * Standaard aan zodra de historie voor elk land hetzelfde voorstel geeft: dan beschrijft de
   * schakelaar wat de klant toch al doet. Verschillen de landen, dan staat hij uit — anders zou het
   * scherm een keuze samenvoegen die de klant bewust uit elkaar had gehouden.
   */
  const alleLandenGelijk = useMemo(
    () => markets.length > 1 && proposals.every((p) => p.length > 0 && zelfdeSet(p, proposals[0] ?? [])),
    [markets.length, proposals],
  );
  const [batchHandmatig, setBatchHandmatig] = useState<boolean | null>(null);
  const batch = markets.length > 1 && (batchHandmatig ?? alleLandenGelijk);

  if (markets.length === 0) return null;

  return (
    <section
      data-testid="intake-retailer-choice"
      className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-[var(--border)] p-3"
    >
      {markets.length > 1 ? (
        <label className="flex items-center gap-2 text-sm" htmlFor="intake-retailer-same-for-all">
          <input
            id="intake-retailer-same-for-all"
            data-testid="intake-retailer-same-for-all"
            type="checkbox"
            checked={batch}
            disabled={disabled}
            onChange={(event) => setBatchHandmatig(event.target.checked)}
          />
          {t('intake.review.retailers.sameForAll')}
        </label>
      ) : null}

      {batch ? (
        <BatchSection markets={markets} histories={histories} choice={choice} disabled={disabled} onChoose={onChoose} />
      ) : markets.length === 1 ? (
        <MarketSection
          market={markets[0]!}
          singleMarket
          history={histories[0]?.data}
          isLoading={histories[0]?.isLoading ?? false}
          chosen={chosenFor(choice, markets[0]!)}
          proposal={proposals[0] ?? []}
          disabled={disabled}
          onChoose={onChoose}
        />
      ) : (
        <>
          <h2 className="text-sm font-semibold text-[var(--foreground-heading)]">{t('intake.review.retailers.title')}</h2>
          {markets.map((market, index) => (
            <MarketSection
              key={market}
              market={market}
              singleMarket={false}
              history={histories[index]?.data}
              isLoading={histories[index]?.isLoading ?? false}
              chosen={chosenFor(choice, market)}
              proposal={proposals[index] ?? []}
              disabled={disabled}
              onChoose={onChoose}
            />
          ))}
        </>
      )}
    </section>
  );
}

interface MarketSectionProps {
  market: string;
  /** Bij één land draagt de kop het land en staat het paneel direct in het blok. */
  singleMarket: boolean;
  history: RetailerHistory | undefined;
  isLoading: boolean;
  chosen: RetailerSelection[];
  proposal: RetailerSelection[];
  disabled?: boolean;
  onChoose: (entries: RetailerChoiceInputEntry[]) => void;
}

function MarketSection({ market, singleMarket, history, isLoading, chosen, proposal, disabled, onChoose }: MarketSectionProps) {
  const { t } = useTranslation();
  /**
   * Dicht zodra er iets te tonen is: wie het eens is met het voorstel hoeft niets te openen. Bij
   * één land én niets gekozen valt er ook niets samen te vatten, en zou een dicht blok werk
   * verbergen; bij meer landen zegt de regel per land zélf al dat er nog iets open staat.
   *
   * De stand volgt de keuze tot de klant zélf klikt, en dat is geen finesse. Het scherm komt binnen
   * met een lege keuze, het voorstel uit de historie wordt daarna bewaard, en pas met dát antwoord
   * staat de keuze er. Werd de stand alleen bij het aankoppelen bepaald, dan zag een klant mét
   * historie voorgoed het open keuzepaneel in plaats van zijn chips.
   */
  const [handmatig, setHandmatig] = useState<boolean | null>(null);
  const open = handmatig ?? (singleMarket && chosen.length === 0);
  const setOpen = setHandmatig;
  const naam = marketName(t, market);

  function toggle(retailer: RetailerSelection, isChosen: boolean) {
    onChoose([
      {
        targetMarket: market,
        retailers: isChosen ? chosen.filter((entry) => entry.gln !== retailer.gln) : [...chosen, retailer],
      },
    ]);
  }

  if (!singleMarket && !open) {
    return (
      <div className="flex items-center justify-between gap-3 border-t border-[var(--border)] pt-2 first:border-t-0 first:pt-0">
        <p className="text-sm">
          <span className="font-medium">{naam}</span>{' '}
          <span data-testid={`intake-retailer-market-summary-${market}`} className="text-[var(--foreground-muted)]">
            {chosen.length === 0 ? t('intake.review.retailers.nothingChosenYet') : chosen.map((entry) => entry.name).join(', ')}
          </span>
        </p>
        <ToggleButton market={market} open={false} onClick={() => setOpen(true)} />
      </div>
    );
  }

  if (singleMarket && !open) {
    return (
      <div className="flex flex-col gap-2">
        <Header market={market} title={t('intake.review.retailers.titleWithMarket', { market: naam })} open={false} onToggle={() => setOpen(true)} />
        <p className="text-sm text-[var(--foreground-muted)]">{t('intake.review.retailers.summary', { count: chosen.length })}</p>
        <ul data-testid={`intake-retailer-chips-${market}`} className="flex flex-wrap gap-2">
          {chosen.map((entry) => (
            <li key={entry.gln} className="rounded-[var(--radius-control)] bg-[var(--background-muted)] px-2 py-1 text-xs">
              {entry.name}
            </li>
          ))}
        </ul>
        {isFromHistory(chosen, proposal) ? (
          <p data-testid={`intake-retailer-from-history-${market}`} className="text-xs text-[var(--foreground-muted)]">
            {t('intake.review.retailers.fromHistory')}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Header
        market={market}
        title={singleMarket ? t('intake.review.retailers.titleWithMarket', { market: naam }) : naam}
        open
        onToggle={() => setOpen(false)}
      />
      {/* FR34-degradatie: informatief blauw, nooit rood of amber, en geen herhaalknop — de klant
          kan gewoon kiezen, en de lijst komt van dezelfde bron als het voorstel. */}
      {history && !history.available ? (
        <p
          role="status"
          data-testid={`intake-retailer-unavailable-${market}`}
          className="rounded-[var(--radius-card)] border border-[var(--info)] bg-[var(--info-light)] p-2 text-sm text-[var(--info-text)]"
        >
          {t('intake.review.retailers.unavailable')}
        </p>
      ) : null}
      {chosen.length === 0 ? <p className="text-sm">{t('intake.review.retailers.emptyQuestion')}</p> : null}
      {isLoading ? (
        <p className="text-sm text-[var(--foreground-muted)]">{t('intake.review.retailers.loading')}</p>
      ) : (
        <RetailerPicker market={market} retailers={history?.retailers ?? []} chosen={chosen} disabled={disabled} onToggle={toggle} />
      )}
    </div>
  );
}

function Header({ market, title, open, onToggle }: { market: string; title: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold text-[var(--foreground-heading)]">{title}</h2>
      <ToggleButton market={market} open={open} onClick={onToggle} />
    </div>
  );
}

function ToggleButton({ market, open, onClick }: { market: string; open: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      data-testid={`intake-retailer-toggle-${market}`}
      onClick={onClick}
      className="text-xs underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
    >
      {open ? t('intake.review.retailers.done') : t('intake.review.retailers.change')}
    </button>
  );
}

interface RetailerPickerProps {
  market: string;
  retailers: RetailerHistoryEntry[];
  chosen: RetailerSelection[];
  disabled?: boolean;
  onToggle: (retailer: RetailerSelection, isChosen: boolean) => void;
}

/**
 * De twee zones met het zoekveld erboven.
 *
 * HET ZOEKVELD FILTERT DOOR ALLEBEI DE ZONES. Zocht het alleen in de dichtgeklapte zone, dan typt
 * een klant "jumbo", ziet niets, en concludeert dat Jumbo niet bestaat — terwijl die al aangevinkt
 * bovenaan staat.
 */
function RetailerPicker({ market, retailers, chosen, disabled, onToggle }: RetailerPickerProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  // Zoeken vouwt de volledige lijst open, maar mag de klant zijn eigen stand niet afpakken: wie de
  // lijst zelf openzette en daarna zijn zoekterm wist, houdt hem open.
  const [alleOpen, setAlleOpen] = useState(false);
  const searchId = useId();
  const gekozenGlns = useMemo(() => new Set(chosen.map((entry) => entry.gln)), [chosen]);

  const query = term.trim().toLowerCase();
  const matcht = (naam: string) => query === '' || naam.toLowerCase().includes(query);

  // De volgorde ís het advies: eerst wat het vaakst gekozen is, daarna op alfabet. Zonder historie
  // is elke telling nul en blijft er precies een alfabetische lijst over.
  const gesorteerd = useMemo(
    () => [...retailers].sort((a, b) => b.timesChosen - a.timesChosen || a.name.localeCompare(b.name)),
    [retailers],
  );

  const gekozen = chosen.filter((entry) => matcht(entry.name));
  const overige = gesorteerd.filter((entry) => !gekozenGlns.has(entry.gln) && matcht(entry.name));

  return (
    <div className="flex flex-col gap-2">
      <label className="sr-only" htmlFor={searchId}>
        {t('intake.review.retailers.searchLabel')}
      </label>
      <input
        id={searchId}
        type="search"
        value={term}
        data-testid={`intake-retailer-search-${market}`}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('intake.review.retailers.search')}
        className="h-8 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
      />

      {gekozen.length > 0 ? (
        <fieldset>
          <legend className="text-xs font-medium text-[var(--foreground-muted)]">
            {t('intake.review.retailers.chosenGroup', { count: chosen.length })}
          </legend>
          <ul data-testid={`intake-retailer-chosen-${market}`}>
            {gekozen.map((entry) => (
              <RetailerRow key={entry.gln} market={market} gln={entry.gln} name={entry.name} checked previously={false} disabled={disabled} onToggle={() => onToggle(entry, true)} />
            ))}
          </ul>
        </fieldset>
      ) : null}

      <details open={alleOpen || query !== ''} onToggle={(event) => setAlleOpen(event.currentTarget.open)}>
        <summary className="cursor-pointer text-xs font-medium text-[var(--foreground-muted)]">
          {t('intake.review.retailers.allGroup', { count: overige.length })}
        </summary>
        <ul data-testid={`intake-retailer-all-${market}`}>
          {overige.map((entry) => (
            <RetailerRow
              key={entry.gln}
              market={market}
              gln={entry.gln}
              name={entry.name}
              checked={false}
              // "Eerder gekozen" beschrijft historie, geen slot: uitvinken mag altijd.
              previously={entry.timesChosen > 0}
              disabled={disabled}
              onToggle={() => onToggle(toSelection(entry), false)}
            />
          ))}
        </ul>
      </details>

      {query !== '' && gekozen.length === 0 && overige.length === 0 ? (
        <p data-testid={`intake-retailer-no-results-${market}`} className="text-sm text-[var(--foreground-muted)]">
          {t('intake.review.retailers.noResults', { term: term.trim() })}
        </p>
      ) : null}
    </div>
  );
}

function RetailerRow({
  market,
  gln,
  name,
  checked,
  previously,
  disabled,
  onToggle,
}: {
  market: string;
  gln: string;
  name: string;
  checked: boolean;
  previously: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between gap-2 py-0.5">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid={`intake-retailer-${market}-${gln}`}
          checked={checked}
          disabled={disabled}
          onChange={onToggle}
          className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        />
        {name}
      </label>
      {previously ? <span className="text-xs text-[var(--foreground-muted)]">{t('intake.review.retailers.previously')}</span> : null}
    </li>
  );
}

function toSelection(entry: RetailerHistoryEntry): RetailerSelection {
  return { gln: entry.gln, name: entry.name };
}

function chosenFor(choice: RetailerChoiceEntry[], market: string): RetailerSelection[] {
  return choice.find((entry) => entry.targetMarket === market)?.retailers ?? [];
}

/**
 * Staat er nog precies het voorstel uit de historie? Alleen dán klopt de regel die zegt waar het
 * vandaan komt. Voorgevinkt zónder die regel leest als een systeeminstelling waar je vanaf moet
 * blijven; de regel bij een keuze die de klant zelf heeft aangepast is gewoon onwaar.
 */
function isFromHistory(chosen: RetailerSelection[], proposal: RetailerSelection[]): boolean {
  if (proposal.length === 0 || chosen.length !== proposal.length) return false;
  const gekozen = new Set(chosen.map((entry) => entry.gln));
  return proposal.every((entry) => gekozen.has(entry.gln));
}

/** Twee keuzes zijn gelijk als ze dezelfde nummers dragen; de volgorde doet er hier niet toe. */
function zelfdeSet(a: RetailerSelection[], b: RetailerSelection[]): boolean {
  if (a.length !== b.length) return false;
  const nummers = new Set(b.map((entry) => entry.gln));
  return a.every((entry) => nummers.has(entry.gln));
}

/** Sleutel voor de gezamenlijke lijst; geen doelmarkt maar de plaats van alle landen samen. */
const ALLE_LANDEN_KEY = 'alle';

interface BatchSectionProps {
  markets: string[];
  histories: ReturnType<typeof useRetailerHistory>;
  choice: RetailerChoiceEntry[];
  disabled?: boolean;
  onChoose: (entries: RetailerChoiceInputEntry[]) => void;
}

/**
 * H13-8 — één keuze voor alle landen.
 *
 * De klant kiest uit alles wat in mínstens één land bestaat; per land bewaren we daarvan wat dáár
 * bestaat. Een afnemer die niet overal voorkomt wordt daarom benoemd: verderop in de keten wordt
 * zo iemand zonder melding weggelaten, en dan denkt de klant dat hij gepubliceerd heeft naar
 * iemand die het nooit krijgt.
 */
function BatchSection({ markets, histories, choice, disabled, onChoose }: BatchSectionProps) {
  const { t } = useTranslation();

  // Zolang één land nog laadt weten we niet wat daar bestaat. Doorklikken zou dat land stil
  // overslaan én een onware regel opleveren ("geldt niet in België") over een lijst die er nog niet
  // is. Dezelfde poort die de weergave per land al heeft.
  const laadt = histories.some((query) => query.isLoading);

  const perMarkt = markets.map((market, index) => ({
    market,
    beschikbaar: new Set((histories[index]?.data?.retailers ?? []).map((entry) => entry.gln)),
    gekozen: chosenFor(choice, market),
  }));

  // Alles wat érgens bestaat, vaakst gekozen eerst — dezelfde volgorde-als-advies als per land.
  const alles = new Map<string, RetailerHistoryEntry>();
  for (const query of histories) {
    for (const entry of query.data?.retailers ?? []) {
      const bestaand = alles.get(entry.gln);
      if (!bestaand || entry.timesChosen > bestaand.timesChosen) alles.set(entry.gln, entry);
    }
  }
  const lijst = [...alles.values()].sort((a, b) => b.timesChosen - a.timesChosen || a.name.localeCompare(b.name));

  const gekozenGlns = new Set(perMarkt.flatMap((m) => m.gekozen.map((entry) => entry.gln)));
  const gekozen = lijst.filter((entry) => gekozenGlns.has(entry.gln)).map(toSelection);

  // Wie is gekozen maar bestaat niet overal? Dat hoort de klant te lezen, niet te ontdekken —
  // verderop in de keten wordt zo iemand zonder melding weggelaten.
  const nietOveral = gekozen
    .map((entry) => ({
      entry,
      missend: perMarkt.filter((m) => !m.beschikbaar.has(entry.gln)).map((m) => marketName(t, m.market)),
    }))
    .filter((r) => r.missend.length > 0);

  /** De regels die de landen gelijktrekken op één keuze — alleen wat werkelijk verandert. */
  function regelsVoor(keuze: RetailerSelection[]): RetailerChoiceInputEntry[] {
    return perMarkt
      .map((m) => ({ targetMarket: m.market, retailers: keuze.filter((entry) => m.beschikbaar.has(entry.gln)), gekozen: m.gekozen }))
      .filter((r) => !zelfdeSet(r.retailers, r.gekozen))
      .map(({ targetMarket, retailers }) => ({ targetMarket, retailers }));
  }

  function toggle(retailer: RetailerSelection, isChosen: boolean) {
    const keuze = isChosen ? gekozen.filter((entry) => entry.gln !== retailer.gln) : [...gekozen, retailer];
    const regels = regelsVoor(keuze);
    // ÉÉN aanroep, hoe verschillend de landen ook zijn. Twee aanroepen dragen hetzelfde
    // versienummer, en dan wint de eerste en krijgt de tweede een 409 — precies de halve keuze die
    // deze vorm moet uitsluiten.
    if (regels.length > 0) onChoose(regels);
  }

  if (laadt) {
    return <p className="text-sm text-[var(--foreground-muted)]">{t('intake.review.retailers.loading')}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <RetailerPicker market={ALLE_LANDEN_KEY} retailers={lijst} chosen={gekozen} disabled={disabled} onToggle={toggle} />
      {nietOveral.length > 0 ? (
        <p data-testid="intake-retailer-not-in-all" className="text-xs text-[var(--foreground-muted)]">
          {nietOveral
            .map((r) => t('intake.review.retailers.notInMarket', { retailer: r.entry.name, market: r.missend.join(', ') }))
            .join(' ')}
        </p>
      ) : null}
    </div>
  );
}
